/**
 * The SLA tab's candidate cards.
 *
 * Split out of SlaTab.tsx when grouping expired interview links into their role
 * cards pushed that file past the 500-line ceiling. Pure presentation: every one
 * of these takes an item and renders it, and the tab owns all the state.
 *
 * The one thing they decide for themselves is WHERE a stuck chip leads — `destinationFor` reads
 * the role's own sections, so a card cannot offer a link to a tab that does not list it.
 */
import type {
  RoleBoard,
  SlaActNowItem,
  SlaAwaitingItem,
  SlaSnoozedItem,
} from '../../types/taActionBoard';
import { CandidateChips } from './CandidateChips';
import { COLORS, fmtDaysAgo } from './format';
import { HandledChip } from './CandidateCard';
import { PhoneChip } from './PhoneChip';
import { SlaStuckChip } from './SlaStuckChip';
import { useState, type ReactNode } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { destinationFor, destinationLabel, type BoardDestination } from './slaChipTarget';

/** A small FINN status dot. aria-hidden: the waiting pill + stuck chip already
 * state each card's status in words, so the dot is a supplemental colour cue. */
export function Dot({ color }: { color: string }) {
  return (
    <span aria-hidden className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
  );
}

/** Shared card frame: identity | stuck-state chip | actions + waiting. */
export function SlaCard({
  dot,
  name,
  leverUrl,
  stageName,
  stuck,
  actions,
  handledLabel = null,
}: {
  dot: ReactNode;
  name: string;
  leverUrl: string;
  stageName: string;
  stuck: ReactNode;
  actions: ReactNode;
  handledLabel?: string | null;
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-gray-200 transition-all${handledLabel ? ' opacity-45' : ''}`}
    >
      <div className="min-w-0 basis-56">
        <div className="flex items-center gap-2 text-sm">
          <span>{dot}</span>
          <span className="truncate font-semibold text-gray-900">{name}</span>
          <a href={leverUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
            Lever ↗
          </a>
        </div>
        <div className="mt-0.5 truncate pl-6 text-xs text-gray-400">{stageName}</div>
      </div>
      {/* middle column: the "where is this stuck?" label — previously empty space */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {handledLabel && <HandledChip label={handledLabel} />}
        {stuck}
      </div>
      <div className="flex shrink-0 items-center gap-2">{actions}</div>
    </div>
  );
}

export function AwaitingCard({
  role,
  item,
  onGoTo,
}: {
  role: RoleBoard;
  item: SlaAwaitingItem;
  onGoTo: (dest: BoardDestination, postingId: string, opportunityId: string) => void;
}) {
  // They have times and have not replied — the move is to re-propose or book, in whichever
  // round they sit. Null when the board lists them in none, which must not become a dead link.
  const dest = destinationFor(role, item.opportunityId, 'awaiting-candidate');
  return (
    <SlaCard
      dot={<Dot color={COLORS.statusOrange} />}
      name={item.name}
      leverUrl={item.leverUrl}
      stageName={item.stageName}
      stuck={
        <SlaStuckChip
          classification="awaiting-candidate"
          onGo={dest ? () => onGoTo(dest, role.postingId, item.opportunityId) : undefined}
          goLabel={dest ? destinationLabel(dest) : undefined}
        />
      }
      actions={
        <>
          <CandidateChips candidateName={item.name} opportunityId={item.opportunityId} />
          <PhoneChip phone={item.phone} size="sm" />
          <span
            className="whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold"
            style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
          >
            {fmtDaysAgo(item.offeredDaysAgo)}
          </span>
        </>
      }
    />
  );
}

export function SnoozedCard({
  item,
  onReleaseSnooze,
}: {
  item: SlaSnoozedItem;
  onReleaseSnooze?: (opportunityId: string) => Promise<void> | void;
}) {
  const [releasing, setReleasing] = useState(false);
  const release = async () => {
    if (!onReleaseSnooze) return;
    setReleasing(true);
    try {
      await onReleaseSnooze(item.opportunityId);
    } catch (e) {
      // Without this the rejection escapes the click handler: the error boundary
      // only covers render, so the button simply stopped spinning and the card
      // stayed snoozed with nothing said. Same shape as sendNewLink.
      showToast({
        status: 'error',
        description:
          e instanceof Error && e.message ? e.message : 'Could not wake this candidate up.',
      });
    } finally {
      setReleasing(false);
    }
  };
  return (
    <SlaCard
      dot={<Dot color={COLORS.statusGray} />}
      name={item.name}
      leverUrl={item.leverUrl}
      stageName={item.stageName}
      stuck={
        <div className="flex min-w-0 flex-col gap-0.5">
          <SlaStuckChip classification="snoozed" snoozedUntil={item.snoozedUntil} />
          {/* The reason is the whole point of a board snooze — show it so the team
              can see WHY this is parked. A Lever-native snooze carries none. */}
          {item.reason && (
            <span className="truncate pl-1 text-xs italic text-gray-500" title={item.reason}>
              “{item.reason}”
            </span>
          )}
        </div>
      }
      actions={
        <>
          <CandidateChips candidateName={item.name} opportunityId={item.opportunityId} />
          <PhoneChip phone={item.phone} size="sm" />
          {onReleaseSnooze && (
            <Button size="sm" variant="outline" onClick={release} disabled={releasing}>
              {releasing ? 'Waking…' : 'Un-snooze'}
            </Button>
          )}
        </>
      }
    />
  );
}

/** The Snooze affordance shared by the act-now and screening cards. */
export function SnoozeButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick}>
      Snooze
    </Button>
  );
}

export function ActNowCard({
  role,
  item,
  handledLabel,
  onGoTo,
  onNudgeFeedback,
  onSnooze,
}: {
  role: RoleBoard;
  item: SlaActNowItem;
  handledLabel: string | null;
  onGoTo: (dest: BoardDestination, postingId: string, opportunityId: string) => void;
  onNudgeFeedback?: (role: RoleBoard, item: SlaActNowItem) => void;
  onSnooze?: (opportunityId: string, name: string, leverUrl: string) => void;
}) {
  const urgent = item.waitingDays >= 5;
  const dest = destinationFor(role, item.opportunityId, item.classification);
  return (
    <SlaCard
      handledLabel={handledLabel}
      dot={<Dot color={urgent ? COLORS.statusRed : COLORS.statusOrange} />}
      name={item.name}
      leverUrl={item.leverUrl}
      stageName={item.stageName}
      stuck={
        <SlaStuckChip
          classification={item.classification}
          onGo={dest ? () => onGoTo(dest, role.postingId, item.opportunityId) : undefined}
          goLabel={dest ? destinationLabel(dest) : undefined}
        />
      }
      actions={
        <>
          <CandidateChips candidateName={item.name} opportunityId={item.opportunityId} />
          {/* Chasing an over-SLA candidate usually means calling them. */}
          <PhoneChip phone={item.phone} size="sm" />
          {/* No "Go to decision" button any more: the stuck chip on this very row IS that link
              now, and for every other actionable state as well. Two controls a centimetre apart
              doing one thing is how the row got crowded in the first place. */}
          {/* The interview happened and the INTERVIEWER owes the feedback —
              the recruiter can do nothing but chase, so give them the chase. */}
          {item.classification === 'feedback-missing' && onNudgeFeedback && (
            <Button size="sm" variant="outline" onClick={() => onNudgeFeedback(role, item)}>
              Nudge for feedback
            </Button>
          )}
          {onSnooze && (
            <SnoozeButton onClick={() => onSnooze(item.opportunityId, item.name, item.leverUrl)} />
          )}
          {/* Waiting time is the whole point of this tab — prominent, top right. */}
          <span
            className="whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-bold"
            style={{
              backgroundColor: urgent ? COLORS.redBg : COLORS.orangeBg,
              color: urgent ? COLORS.redText : COLORS.orangeText,
            }}
          >
            {item.waitingDays.toFixed(1)} days
          </span>
        </>
      }
    />
  );
}
